import { useCallback } from "react";
import { useLocation, useNavigate, type NavigateOptions } from "react-router-dom";
import {
  buildAppNavigationUrl,
  openAppNavigation,
  type AppNavigationTarget,
  type NavigationContext,
} from "@/lib/navigation/appNavigation";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";

export type OpenNavigationOptions = {
  replace?: boolean;
  navigateOptions?: NavigateOptions;
  onBeforeSameTabNavigate?: () => void;
  navigationMode?: "push" | "replace";
};

export const useOpenAppNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const resetWorkspaceDocumentVersionStore =
    useWorkspaceDocumentVersionStore(
      (s) => s.resetWorkspaceDocumentVersionStore
    );

  const navigationContext: NavigationContext = location.pathname.includes(
    "/chat/"
  )
    ? "chat"
    : "app";

  const openNavigation = useCallback(
    (
      target: AppNavigationTarget,
      event?: React.MouseEvent | MouseEvent,
      options?: OpenNavigationOptions
    ): boolean => {
      return openAppNavigation({
        target,
        navigate,
        event,
        replace: options?.replace,
        navigateOptions: options?.navigateOptions,
        onBeforeSameTabNavigate: options?.onBeforeSameTabNavigate,
        onWorkspaceDocumentNavigate: resetWorkspaceDocumentVersionStore,
        navigationMode: options?.navigationMode,
      });
    },
    [navigate, resetWorkspaceDocumentVersionStore]
  );

  const buildNavigationUrl = useCallback(
    (target: AppNavigationTarget): string | null => {
      return buildAppNavigationUrl(target, { pathname: location.pathname });
    },
    [location.pathname]
  );

  return {
    openNavigation,
    buildNavigationUrl,
    navigationContext,
    resetWorkspaceDocumentVersionStore,
  };
};
